import React from 'react'

import { useIntersectionObserverContext } from '../../services/IntersectionObserverContext'
import { Apps } from './Apps'
import { Domain } from './Domain'
import TrafficDomainSelect from './TrafficDomainSelect'
import TrafficHeader from './TrafficHeader'
import { ParsedTrafficData } from './types'
import { ParsedTrafficItem } from './types'
import { sortByMauLastValueDesc } from './utils'

interface Props {
  parsedTrafficData: ParsedTrafficData
  domain: string
  index: number
}

export function TrafficDomainSection({ parsedTrafficData, domain, index }: Props) {
  const { registerIntersectionObservedElement } = useIntersectionObserverContext()
  const domainApps = React.useMemo(
    () => sortByMauLastValueDesc(parsedTrafficData.appsWithPeers.filter((app) => app.domain === domain)),
    [parsedTrafficData, domain]
  )
  const [isInView, setIsInView] = React.useState(false)
  const [hasBeenInView, setHasBeenInView] = React.useState(false)
  const { mainTraffic, peerTraffics, industryTraffics, peerApps, industryApps } = React.useMemo(() => {
    const peerTraffics: ParsedTrafficItem[] = []
    const industryTraffics: ParsedTrafficItem[] = []

    const mainTraffic = parsedTrafficData.trafficsWithPeers.find((traffic) => traffic.domain === domain)!

    parsedTrafficData?.trafficsWithPeers.forEach((traffic) => {
      if (traffic.domain === domain) {
        return
      } else if (mainTraffic.peers?.includes(traffic.domain!)) {
        peerTraffics.push(traffic)
      } else if (
        mainTraffic.heatmapSector &&
        mainTraffic.heatmapSubSector &&
        mainTraffic.heatmapSector === traffic.heatmapSector &&
        mainTraffic.heatmapSubSector === traffic.heatmapSubSector
      ) {
        industryTraffics.push(traffic)
      }
    })

    const peerApps = sortByMauLastValueDesc(
      parsedTrafficData.appsWithPeers.filter((app) => peerTraffics.some((traffic) => app.domain === traffic.domain))
    )
    const industryApps = sortByMauLastValueDesc(
      parsedTrafficData.appsWithPeers.filter((app) => industryTraffics.some((traffic) => app.domain === traffic.domain))
    )

    return {
      mainTraffic,
      peerTraffics,
      industryTraffics,
      peerApps,
      industryApps,
    }
  }, [parsedTrafficData, domain])

  const domainItems = React.useMemo(
    () =>
      parsedTrafficData.trafficsWithPeers
        .filter(({ ticker, domain }) => domain && ticker === mainTraffic.ticker)
        .map(({ domain }) => ({ value: domain!, label: domain! })),
    [parsedTrafficData.trafficsWithPeers, mainTraffic.ticker]
  )

  React.useEffect(() => {
    if (isInView && !hasBeenInView) {
      setHasBeenInView(true)
    }
  }, [hasBeenInView, isInView])

  React.useEffect(() => {
    const unregister = registerIntersectionObservedElement(domain, setIsInView)

    return () => {
      unregister?.()
    }
  }, [domain, registerIntersectionObservedElement])

  return (
    <div id={domain}>
      {index > 0 && <div className="w-full border-t border-dashed border-secondary"></div>}
      <TrafficHeader
        domainElement={
          domainItems.length > 1 ? (
            <TrafficDomainSelect domain={domain} domainItems={domainItems} isInteractive={isInView} />
          ) : (
            domain
          )
        }
        sector={mainTraffic.heatmapSector}
        subSector={mainTraffic.heatmapSubSector}
        monthlyVisits={mainTraffic.traffic?.v ?? []}
      />

      <Domain
        mainTraffic={mainTraffic}
        peerTraffics={peerTraffics}
        industryTraffics={industryTraffics}
        hasBeenInView={hasBeenInView}
        isInView={isInView}
      />

      {domainApps.length > 0 && (
        <Apps
          domainApps={domainApps}
          peerApps={peerApps}
          industryApps={industryApps}
          hasBeenInView={hasBeenInView}
          isInView={isInView}
        />
      )}
    </div>
  )
}
